/** Панель раздела «Апвеллинг»: чем закрасить и что наложить линиями.
 *
 *  РАДИОГРУППА, а не семь новых `ModelKind`. Раздел — это набор асинхронных
 *  эффектов (фон, опрос после «Загрузить», перенацеливание probability,
 *  `seriesRef`), и семь разделов размножили бы эту машинерию всемеро. Механизм —
 *  параметр раздела, как глубина.
 *
 *  Список строится ИЗ КАТАЛОГА слоёв, а не из перечня здесь: перечень отстанет
 *  от каталога, и добавленный на сервере механизм в панели не появится, а
 *  снятый оставит мёртвый тумблер, отвечающий 400.
 */
import { useT } from '../i18n'
import type { Mechanism } from './upwellingSelection'

interface Props {
  catalog: Mechanism[]
  /** Какие механизмы посчитаны за выбранную дату (missing = ещё не спрашивали). */
  present: Set<string> | null
  raster: string
  iso: string[]
  zero: boolean
  onRaster: (id: string) => void
  onToggleIso: (id: string) => void
  onZero: (v: boolean) => void
}

export function UpwellingControl(p: Props) {
  const t = useT()
  if (p.catalog.length === 0) return null

  return (
    <div className="space-y-3">
      <section>
        <div className="eyebrow mb-1.5">{t('upw.fillTitle')}</div>
        <div className="flex flex-col gap-1">
          {p.catalog.map(m => {
            // «Посчитано ли» показываем, только когда ответ на руках: до него
            // серая строка врала бы про пустоту при живом поле.
            const missing = p.present != null && !p.present.has(m.id)
            return (
              <label
                key={m.id}
                className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${
                  p.raster === m.id ? 'bg-hull/60 text-foam' : 'text-mist hover:text-foam'
                }`}
              >
                <input
                  type="radio"
                  name="upw-raster"
                  className="accent-sonar"
                  checked={p.raster === m.id}
                  onChange={() => p.onRaster(m.id)}
                />
                <span className="flex-1">{t(`layers.${m.id}`)}</span>
                <span className="text-[10px] text-faint">{t(`units.${m.unit}`)}</span>
                {missing && (
                  <span className="text-[10px] text-faint" title={t('upw.noData')}>—</span>
                )}
              </label>
            )
          })}
        </div>
      </section>

      <section>
        <div className="eyebrow mb-1.5">{t('upw.isoTitle')}</div>
        <div className="flex flex-col gap-1">
          {p.catalog
            // Заливку в listKey линий не предлагаем вовсе: контур поля, которое
            // ты же и красишь, ложится ровно по границе своих же тонов.
            .filter(m => m.id !== p.raster)
            .map(m => (
              <label
                key={m.id}
                className="flex cursor-pointer items-center gap-2 px-2 py-1 text-sm text-mist hover:text-foam"
              >
                <input
                  type="checkbox"
                  className="accent-sonar"
                  checked={p.iso.includes(m.id)}
                  onChange={() => p.onToggleIso(m.id)}
                />
                <span className="flex-1">{t(`layers.${m.id}`)}</span>
              </label>
            ))}
        </div>
      </section>

      <label className="flex cursor-pointer items-center gap-2 px-2 text-sm text-mist hover:text-foam">
        <input
          type="checkbox"
          className="accent-sonar"
          checked={p.zero}
          onChange={e => p.onZero(e.target.checked)}
        />
        <span className="flex-1">{t('upw.zeroLine')}</span>
      </label>
      <div className="px-2 text-[10px] text-faint">{t('upw.zeroHint')}</div>
    </div>
  )
}
